import React from 'react'
import { UploadAreaProps } from '../../types'
import { FILE_UPLOAD } from '../../constants'
import { useTranslate } from '../../hooks/useTranslate'

const UploadArea: React.FC<UploadAreaProps> = ({
    dragActive,
    analyzing,
    onDrag,
    onDrop,
    onFileSelect,
    openFileDialog,
    fileInputRef
}) => {
    const { t } = useTranslate()
    return (
        <div className="dd-upload-wrapper">
            <div
                className={`dd-upload-area ${
                    dragActive
                        ? 'drag-active'
                        : analyzing
                            ? 'analyzing'
                            : ''
                }`}
                onDragEnter={onDrag}
                onDragLeave={onDrag}
                onDragOver={onDrag}
                onDrop={onDrop}
                role="button"
                tabIndex={0}
                aria-label="Upload data file by dragging or activating to browse"
                aria-busy={analyzing || undefined}
                onKeyDown={(e) => { if (!analyzing && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); openFileDialog() } }}
                data-upload-drop-zone
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={FILE_UPLOAD.ACCEPT_ATTRIBUTE}
                    onChange={onFileSelect}
                    className="hidden"
                />

                {analyzing ? (
                    <div className="text-center" style={{ padding: `clamp(1rem, 1.5vh + 0.5rem, 1.8rem) 0` }} aria-live="polite">
                        <div className="flex items-center justify-center" style={{ marginBottom: `clamp(1rem, 1vh + 0.5rem, 1.4rem)` }}>
                            <div className="loader-red loader-xl" style={{ transform: `scale(clamp(0.9, 0.02vw + 0.9, 1.2))` }}></div>
                        </div>
                        <h3 className="font-bold text-gray-900" style={{ fontSize: `clamp(1.3rem, 1.2vh + 0.8rem, 1.6rem)`, marginBottom: `clamp(0.6rem, 0.8vh + 0.4rem, 0.8rem)`, lineHeight: '1.3' }}>{t('Data Doctor is analyzing your dataset')}</h3>
                        <p className="text-gray-600" style={{ fontSize: `clamp(1rem, 0.8vh + 0.7rem, 1.2rem)`, lineHeight: '1.4' }}>{t('Processing your data and preparing column configuration...')}</p>
                    </div>
                ) : (
                    <div className="text-center">
                        <div className="flex items-center justify-center" style={{ marginBottom: `clamp(0.8rem, 1.8vh, 1.5rem)` }}>
                            <div 
                                className="bg-gradient-to-br from-red-600 to-red-700 rounded-full shadow-2xl transform hover:scale-110 transition-transform duration-300"
                                style={{ padding: `clamp(0.6rem, 1.4vh, 1.2rem)` }}
                            >
                                <svg className="text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: `clamp(1.8rem, 2.8vh, 2.8rem)`, height: `clamp(1.8rem, 2.8vh, 2.8rem)` }}>
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                </svg>
                            </div>
                        </div>
                        <h3 className="font-bold text-gray-900" style={{ fontSize: `clamp(1.2rem, 2.2vh, 1.8rem)`, marginBottom: `clamp(0.5rem, 1.2vh, 1rem)`, lineHeight: '1.2' }}>{t('Upload Data File')}</h3>
                        <p className="text-gray-600 max-w-md mx-auto" style={{ fontSize: `clamp(0.85rem, 1.5vh, 1.2rem)`, marginBottom: `clamp(0.8rem, 1.8vh, 1.4rem)`, lineHeight: '1.4' }}>
                            {t('Drag & drop your file here or click to browse')}
                        </p>

                        <button
                            onClick={openFileDialog}
                            className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                            style={{ 
                                padding: `clamp(0.5rem, 1.2vh, 0.8rem) clamp(1rem, 2.2vh, 1.6rem)`,
                                fontSize: `clamp(0.85rem, 1.5vh, 1.2rem)`,
                                marginBottom: `clamp(0.8rem, 1.8vh, 1.4rem)`
                            }}
                        >
                            {t('Choose File')}
                        </button>

                        <div className="flex items-center justify-center text-gray-500" style={{ gap: `clamp(0.5rem, 1.2vh, 0.8rem)`, fontSize: `clamp(0.75rem, 1.3vh, 1rem)` }}>
                            <div className="bg-red-50 rounded-lg border-2 border-red-200" style={{ padding: `clamp(0.25rem, 0.6vh, 0.5rem) clamp(0.5rem, 1.2vh, 0.8rem)` }}>
                                <span className="font-medium text-red-700">CSV</span>
                            </div>
                            <span className="text-red-400" style={{ fontSize: `clamp(0.75rem, 1.3vh, 1rem)` }}>•</span>
                            <div className="bg-red-50 rounded-lg border-2 border-red-200" style={{ padding: `clamp(0.25rem, 0.6vh, 0.5rem) clamp(0.5rem, 1.2vh, 0.8rem)` }}>
                                <span className="font-medium text-red-700">Excel</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default UploadArea